import styled from 'styled-components';

export const auth = {
  purple: '#6B21A8',
  purpleDeep: '#2e0a4f',
  purpleMid: '#4c1d95',
  purpleBright: '#8b5cf6',
  purpleLight: '#a78bfa',
  purpleTint: '#f6f3ff',
  bg: '#f8fafc',
  text: '#0f172a',
  muted: '#64748b',
  card: '#ffffff',
  line: 'rgba(15,23,42,0.08)',
  font: 'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
};

export const Page = styled.section`
  width: 100%;
  flex: 1;
  min-height: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 28px 20px 56px;
  box-sizing: border-box;
  font-family: ${auth.font};
  color: ${auth.text};
  background:
    radial-gradient(900px 420px at 12% -10%, rgba(167, 139, 250, 0.16), transparent 58%),
    radial-gradient(700px 380px at 100% 0%, rgba(107, 33, 168, 0.1), transparent 55%),
    ${auth.bg};
`;

export const Shell = styled.div<{ $wide?: boolean }>`
  width: 100%;
  max-width: ${({ $wide }) => ($wide ? '1040px' : '920px')};
  display: grid;
  grid-template-columns: ${({ $wide }) => (
    $wide ? 'minmax(240px, 0.85fr) minmax(340px, 1.15fr)' : 'minmax(260px, 1fr) minmax(300px, 1.05fr)'
  )};
  gap: 0;
  align-items: stretch;
  border-radius: 28px;
  overflow: hidden;
  background: ${auth.card};
  border: 1px solid ${auth.line};
  box-shadow:
    0 24px 60px -28px rgba(46, 10, 79, 0.35),
    0 8px 24px rgba(15, 23, 42, 0.04);
  @media (max-width: 880px) {
    grid-template-columns: 1fr;
    border-radius: 22px;
  }
`;

export const BrandPanel = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0;
  padding: 44px 40px;
  color: #fff;
  background:
    radial-gradient(700px 360px at 8% -20%, rgba(167, 139, 250, 0.38), transparent 62%),
    radial-gradient(520px 300px at 110% 120%, rgba(107, 33, 168, 0.55), transparent 60%),
    linear-gradient(155deg, ${auth.purpleDeep} 0%, ${auth.purpleMid} 55%, ${auth.purple} 100%);
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.07), transparent 38%);
  }
  > * {
    position: relative;
    z-index: 1;
  }
  @media (max-width: 880px) {
    padding: 32px 24px 28px;
  }
`;

export const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  width: fit-content;
  margin: 0 0 18px;
  padding: 7px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.18);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

export const Title = styled.h1`
  margin: 0;
  font-size: clamp(30px, 4vw, 42px);
  line-height: 1.08;
  letter-spacing: -0.032em;
  font-weight: 700;
  em {
    font-style: normal;
    background: linear-gradient(92deg, #ede9fe 10%, ${auth.purpleLight} 90%);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
  }
`;

export const Lead = styled.p`
  margin: 16px 0 0;
  max-width: 34ch;
  font-size: 15.5px;
  line-height: 1.55;
  color: rgba(255, 255, 255, 0.78);
`;

export const Assurances = styled.ul`
  list-style: none;
  margin: 28px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const Assurance = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13.5px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.88);
  &::before {
    content: '';
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;
    background: linear-gradient(135deg, #fde68a, #fbbf24);
    box-shadow: 0 0 0 3px rgba(251, 191, 36, 0.2);
  }
`;

export const FormPanel = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 40px 36px;
  background: ${auth.card};
  @media (max-width: 880px) {
    padding: 28px 22px 32px;
  }
`;

export const FormTitle = styled.h2`
  margin: 0 0 6px;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${auth.text};
`;

export const FormSub = styled.p`
  margin: 0 0 22px;
  font-size: 14px;
  line-height: 1.45;
  color: ${auth.muted};
`;

export const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
  font-size: 13px;
  font-weight: 600;
  color: ${auth.text};
`;

export const Input = styled.input<{ $error?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  padding: 11px 12px;
  border-radius: 10px;
  border: 1px solid ${({ $error }) => ($error ? '#b91c1c' : 'rgba(15, 23, 42, 0.12)')};
  font: inherit;
  font-size: 15px;
  font-weight: 400;
  color: ${auth.text};
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  &:focus {
    outline: none;
    border-color: ${({ $error }) => ($error ? '#b91c1c' : 'rgba(107, 33, 168, 0.45)')};
    box-shadow: 0 0 0 3px ${({ $error }) => (
      $error ? 'rgba(185, 28, 28, 0.12)' : 'rgba(107, 33, 168, 0.12)'
    )};
  }
  &::placeholder {
    color: #94a3b8;
  }
`;

export const Select = styled.select<{ $error?: boolean }>`
  width: 100%;
  box-sizing: border-box;
  padding: 11px 12px;
  border-radius: 10px;
  border: 1px solid ${({ $error }) => ($error ? '#b91c1c' : 'rgba(15, 23, 42, 0.12)')};
  font: inherit;
  font-size: 15px;
  font-weight: 400;
  color: ${auth.text};
  background: #fff;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  &:focus {
    outline: none;
    border-color: ${({ $error }) => ($error ? '#b91c1c' : 'rgba(107, 33, 168, 0.45)')};
    box-shadow: 0 0 0 3px ${({ $error }) => (
      $error ? 'rgba(185, 28, 28, 0.12)' : 'rgba(107, 33, 168, 0.12)'
    )};
  }
`;

export const Error = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #b91c1c;
`;

export const Hint = styled.p`
  margin: 0 0 8px;
  font-size: 13px;
  line-height: 1.45;
  color: ${auth.muted};
`;

export const LinkRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin: 4px 0 18px;
`;

export const TextLink = styled.button`
  border: none;
  background: none;
  padding: 0;
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  color: ${auth.purple};
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
`;

export const PrimaryBtn = styled.button`
  width: 100%;
  margin-top: 8px;
  padding: 13px 18px;
  border: none;
  border-radius: 10px;
  font: inherit;
  font-size: 16px;
  font-weight: 600;
  color: #fff;
  cursor: pointer;
  background: linear-gradient(90deg, ${auth.purple}, ${auth.purpleBright});
  transition: filter 0.15s ease, transform 0.12s ease, box-shadow 0.15s ease;
  &:hover:not(:disabled) {
    filter: brightness(0.96);
    transform: translateY(-1px);
    box-shadow: 0 10px 24px rgba(107, 33, 168, 0.18);
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
`;

export const GhostBtn = styled.button`
  padding: 12px 18px;
  border-radius: 10px;
  border: 1px solid rgba(107, 33, 168, 0.16);
  background: #fff;
  color: ${auth.purple};
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease, transform 0.12s ease;
  &:hover:not(:disabled) {
    background: ${auth.purple};
    color: #fff;
    border-color: ${auth.purple};
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

export const BtnRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
  flex-wrap: wrap;
  ${GhostBtn}, ${PrimaryBtn} {
    width: auto;
    flex: 1;
    margin-top: 0;
    min-width: 120px;
  }
`;

export const PasswordWrap = styled.div`
  position: relative;
  ${Input} {
    padding-right: 44px;
  }
`;

export const EyeBtn = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: ${auth.muted};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: 6px;
  &:hover {
    color: ${auth.text};
    background: ${auth.purpleTint};
  }
`;
